const ReplaceTires = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/replace-tires.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            When Should You Replace Your Tires?
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Tires play a vital role in your vehicle’s safety and performance. Knowing when to replace them can protect you from accidents, improve fuel efficiency, and ensure a smoother ride.
          </p>
        </div>
      </div>

      <section className="w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            Many drivers assume tires only need replacing when they go flat — but worn-out tires can quietly compromise your safety long before that. At <strong>Express Tire Works</strong>, we believe every driver should know the clear signs of tire wear to avoid blowouts, hydroplaning, and reduced braking performance.  
          </p>
          <p className="mb-6">
            In this extensive guide, we help you understand how to recognize tire wear, when to replace your tires, and why timely replacement makes a dramatic difference in your vehicle’s handling and safety.
          </p>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              1. Check the Tread Depth
            </h2>
            <p>
              Tread depth is one of the most reliable indicators of tire health. The legal minimum tread depth in most regions is <strong>2/32 of an inch</strong>. Anything below this significantly reduces traction, especially in wet conditions.
            </p>
            <p className="mt-3">
              You can check tread depth using:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Penny Test:</strong> If you see the top of Lincoln’s head, your tires are too worn.</li>
              <li><strong>Tread Depth Gauge:</strong> A more accurate tool for measuring remaining tread.</li>
              <li><strong>Tread Wear Indicators:</strong> Built-in bars that become visible when your tires reach minimum depth.</li>
            </ul>
            <p>
              If your tread depth is at or near the limit, it’s time for replacement — even if the tires “look fine.”
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              2. Look for Uneven Wear Patterns
            </h2>
            <p>
              Uneven wear is a sign that something may be wrong with your alignment, suspension, or tire pressure. Even if only one area is worn, the entire tire becomes unsafe.
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Center Wear:</strong> Overinflated tires.</li>
              <li><strong>Edge Wear:</strong> Underinflated tires.</li>
              <li><strong>Cupping or scalloping:</strong> Suspension issues causing irregular bouncing.</li>
              <li><strong>One-sided wear:</strong> Misalignment that must be corrected.</li>
            </ul>
            <p>
              When uneven wear becomes visible, replacing the affected tire(s) is essential — and schedule an alignment check immediately to prevent premature wear on new tires.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              3. Check the Tire Age
            </h2>
            <p>
              Even if your tires have plenty of tread, age alone can make them unsafe. Rubber naturally degrades over time due to heat, sunlight, and oxygen exposure.
            </p>
            <p className="mt-3">
              Most experts recommend replacing tires every:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>6 years:</strong> Ideal replacement interval for most vehicles.</li>
              <li><strong>10 years max:</strong> Tires should not be used beyond this period — even as spares.</li>
            </ul>
            <p>
              You can find your tire’s manufacturing date on the sidewall as a 4-digit DOT code. For example, <strong>0321</strong> means the tire was manufactured in the 3rd week of 2021.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              4. Visible Damage or Cracks
            </h2>
            <p>
              Tires constantly face extreme road and weather conditions. Over time, cracks, cuts, or bulges may appear — each one signaling potential failure.
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Sidewall cracks:</strong> Usually caused by aging or long-term sun exposure.</li>
              <li><strong>Bulges:</strong> Often indicate internal structural damage from impact.</li>
              <li><strong>Deep cuts:</strong> May cause air loss or sudden blowouts.</li>
            </ul>
            <p>
              If you notice any of these signs, replace your tire immediately. Damaged tires are unsafe even if tread levels are adequate.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              5. Poor Handling or Increased Vibrations
            </h2>
            <p>
              If your car suddenly feels different — less stable, louder on the road, or more prone to vibration — worn tires may be the cause.
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Longer braking distances</li>
              <li>Shaky steering at certain speeds</li>
              <li>Poor traction in rain or snow</li>
              <li>Difficulty maintaining lane stability</li>
            </ul>
            <p>
              These symptoms indicate that your tires can no longer grip the road effectively, compromising your safety.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              6. Frequent Air Pressure Loss
            </h2>
            <p>
              If a tire loses air more quickly than normal, it may have internal damage, a slow leak, or compromised bead seating. Constantly refilling air is not a solution — it’s a warning.
            </p>
            <p className="mt-3">
              A tire losing 1–3 PSI monthly is normal. Anything more suggests:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Punctures</li>
              <li>Corroded wheel rims</li>
              <li>Valve stem failure</li>
              <li>Sidewall damage</li>
            </ul>
            <p>
              Replace the tire if the underlying issue cannot be safely repaired.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              7. Seasonal Tire Replacement
            </h2>
            <p>
              If you use seasonal tires (summer or winter), replacing them when their performance drops is crucial. Winter tires become ineffective when the tread wears down, and summer tires lose grip as rubber hardens with age.
            </p>
            <p className="mt-3">
              Switching at the right time helps optimize:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Safety</li>
              <li>Traction</li>
              <li>Fuel efficiency</li>
              <li>Overall handling</li>
            </ul>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              Final Thoughts
            </h2>
            <p>
              Replacing your tires at the right time is essential for safe and reliable driving. By paying attention to tread depth, tire age, wear patterns, and performance changes, you can prevent accidents and extend the life of your vehicle.  
              At <strong>Express Tire Works</strong>, our experts are always available to help you inspect your tires and choose replacements that fit your driving needs and budget.
            </p>
            <p className="mt-3">
              When it comes to safety, don’t wait — worn tires are one of the most preventable hazards on the road.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Tire Replacement</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Vehicle Safety</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Tire Wear Signs</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Tire Maintenance</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Auto Care Tips</p>
        </div>
      </section>
    </div>
  );
};

export default ReplaceTires;